import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { demenageurUploadUrl } from '../../utils/demenageurUploadUrl';
import {
  FiUser,
  FiTruck,
  FiShield,
  FiMail,
  FiPhone,
  FiCheckCircle,
  FiAlertCircle,
  FiImage,
  FiExternalLink,
} from 'react-icons/fi';
import { formatUserPhoneDisplay } from '../../utils/resolveUserPhone';

export function formatServiceType(value) {
  if (!value) return '—';
  const v = String(value).toLowerCase();
  if (v === 'local') return 'Local';
  if (v === 'international') return 'International';
  return String(value);
}

function formatDate(dateString) {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function Field({ label, value, mono, className = '' }) {
  return (
    <div className={className}>
      <dt className="text-gray-500 text-xs mb-1">{label}</dt>
      <dd className={`text-white text-sm ${mono ? 'font-mono' : 'font-medium'}`}>{value ?? '—'}</dd>
    </div>
  );
}

function DocumentCard({ label, filename, side, srcOverride }) {
  const [broken, setBroken] = useState(false);
  const src = srcOverride || demenageurUploadUrl(filename);
  if (!src) return null;
  return (
    <div className="rounded-xl border border-gray-700 bg-gray-900/60 overflow-hidden">
      <div className="px-3 py-2 bg-gray-800/80 border-b border-gray-700 text-xs font-semibold text-gray-400">
        {label} — {side}
      </div>
      <a href={src} target="_blank" rel="noopener noreferrer" className="block">
        {broken ? (
          <div className="min-h-[120px] flex flex-col items-center justify-center text-gray-400 text-sm p-4 gap-2">
            <FiImage className="w-8 h-8 opacity-50" />
            <span>Aperçu indisponible</span>
            <span className="text-yellow-500/90 text-xs break-all">{filename}</span>
          </div>
        ) : (
          <img
            src={src}
            alt={`${label} ${side}`}
            className="w-full max-h-72 object-contain bg-black/40"
            onError={() => setBroken(true)}
          />
        )}
      </a>
    </div>
  );
}

function SinglePhotoSection({ title, filename, url }) {
  if (!filename && !url) {
    return (
      <div className="rounded-xl border border-dashed border-gray-600 p-6 text-gray-500 text-sm">
        {title} : aucune photo enregistrée.
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <h4 className="text-md font-bold text-white flex items-center gap-2">
        <FiTruck className="text-yellow-500" />
        {title}
      </h4>
      <div className="grid grid-cols-1 max-w-md">
        <DocumentCard label={title} filename={filename} side="Photo" srcOverride={url} />
      </div>
    </div>
  );
}

function DocumentSection({ title, doc, labels }) {
  if (!doc || typeof doc !== 'object') {
    return (
      <div className="rounded-xl border border-dashed border-gray-600 p-6 text-gray-500 text-sm">
        {title} : aucun fichier enregistré.
      </div>
    );
  }
  const front = doc.front || doc.recto;
  const back = doc.back || doc.verso;
  const frontUrl = doc.frontUrl || doc.rectoUrl;
  const backUrl = doc.backUrl || doc.versoUrl;
  return (
    <div className="space-y-3">
      <h4 className="text-md font-bold text-white flex items-center gap-2">
        <FiShield className="text-yellow-500" />
        {title}
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {front && <DocumentCard label={title} filename={front} side={labels.front} srcOverride={frontUrl} />}
        {back && <DocumentCard label={title} filename={back} side={labels.back} srcOverride={backUrl} />}
      </div>
    </div>
  );
}

/** Affiche les données du formulaire d'inscription (déménageur). */
export default function UserRegistrationDetailsView({ payload, userId, showVerificationLink = true }) {
  const user = payload?.user;
  const profile = payload?.profile;
  const vehiclePhotoFilename =
    payload?.vehiclePhoto ||
    profile?.vehicle_photo ||
    payload?.user?.registration_vehicle_photo ||
    null;
  const vehiclePhotoUrl =
    payload?.vehiclePhotoUrl ||
    profile?.vehicle_photo_url ||
    demenageurUploadUrl(vehiclePhotoFilename);

  if (!user) {
    return (
      <p className="text-gray-500 text-sm py-4">Données d&apos;inscription non disponibles.</p>
    );
  }

  return (
    <div className="mt-6 pt-6 border-t border-gray-700 space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-lg font-bold text-white flex items-center">
          <FiTruck className="mr-2 text-yellow-400" />
          Données du formulaire d&apos;inscription
        </h3>
        {showVerificationLink && userId && (
          <Link
            to={`/admin/mover-registration/${userId}`}
            className="inline-flex items-center gap-2 text-sm text-yellow-400 hover:text-yellow-300 font-medium"
          >
            <FiExternalLink />
            Fiche vérification complète
          </Link>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-gray-700 bg-gray-900/50 p-6 space-y-4">
          <h4 className="text-base font-bold text-yellow-400 flex items-center gap-2">
            <FiUser /> Identité & compte
          </h4>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Prénom" value={user.first_name} />
            <Field label="Nom" value={user.last_name} />
            <div className="sm:col-span-2 flex items-start gap-2">
              <FiMail className="text-gray-500 mt-1 shrink-0" />
              <Field label="Email" value={user.email} className="flex-1" />
            </div>
            <div className="sm:col-span-2 flex items-start gap-2">
              <FiPhone className="text-gray-500 mt-1 shrink-0" />
              <Field label="Téléphone" value={formatUserPhoneDisplay(user)} className="flex-1" />
            </div>
            <Field label="Adresse" value={user.address} />
            <Field label="Statut compte" value={user.status} />
            <div>
              <dt className="text-gray-500 text-xs mb-1">Compte vérifié (admin)</dt>
              <dd>
                {user.is_verified ? (
                  <span className="inline-flex items-center gap-1 text-green-400 text-sm font-medium">
                    <FiCheckCircle /> Oui
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-amber-400 text-sm font-medium">
                    <FiAlertCircle /> En attente
                  </span>
                )}
              </dd>
            </div>
            {user.phone_verified !== undefined && (
              <div>
                <dt className="text-gray-500 text-xs mb-1">Téléphone vérifié (SMS)</dt>
                <dd className="text-white text-sm">{user.phone_verified ? 'Oui' : 'Non'}</dd>
              </div>
            )}
            <Field label="Inscription" value={formatDate(user.created_at)} />
            {user.passwordInfo && (
              <div className="sm:col-span-2">
                <dt className="text-gray-500 text-xs mb-1">Mot de passe</dt>
                <dd className="text-amber-200/90 text-sm leading-relaxed">{user.passwordInfo}</dd>
              </div>
            )}
          </dl>
        </div>

        <div className="rounded-2xl border border-gray-700 bg-gray-900/50 p-6 space-y-4">
          <h4 className="text-base font-bold text-yellow-400 flex items-center gap-2">
            <FiTruck /> Profil métier (inscription)
          </h4>
          {profile ? (
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Type de service" value={formatServiceType(profile.service_type)} />
              <Field label="Type de véhicule" value={profile.vehicle_type} />
              {profile.country != null && profile.country !== '' && (
                <Field label="Pays (international)" value={String(profile.country)} />
              )}
              <Field label="N° CIN / pièce d'identité" value={profile.cin_number} mono />
              {profile.rating != null && (
                <Field label="Note moyenne" value={`${Number(profile.rating).toFixed(1)} / 5`} />
              )}
              {profile.total_reviews != null && (
                <Field label="Nombre d'avis" value={String(profile.total_reviews)} />
              )}
            </dl>
          ) : (
            <p className="text-gray-500 text-sm">Aucune ligne dans mover_profiles.</p>
          )}
        </div>
      </div>

      {(user.trial_start_date || user.trial_end_date || user.mission_credits_balance != null) && (
        <div className="rounded-2xl border border-gray-700 bg-gray-900/50 p-6">
          <h4 className="text-base font-bold text-white mb-4">Essai & crédits missions</h4>
          <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <Field label="Début essai" value={formatDate(user.trial_start_date)} />
            <Field label="Fin essai" value={formatDate(user.trial_end_date)} />
            <Field label="Essai utilisé" value={user.trial_used ? 'Oui' : 'Non'} />
            {user.mission_credits_balance != null && (
              <Field label="Crédits missions" value={String(user.mission_credits_balance)} />
            )}
          </dl>
        </div>
      )}

      <div className="space-y-8">
        <SinglePhotoSection
          title="Photo du camion"
          filename={vehiclePhotoFilename}
          url={vehiclePhotoUrl}
        />
        <DocumentSection title="Permis de conduire" doc={user.permis} labels={{ front: 'Recto', back: 'Verso' }} />
        <DocumentSection title="Carte d'identité" doc={user.carte_cin} labels={{ front: 'Recto', back: 'Verso' }} />
        <DocumentSection title="Carte grise" doc={user.carte_grise} labels={{ front: 'Recto', back: 'Verso' }} />
      </div>
    </div>
  );
}
